import { FC, Children, createElement } from 'react';
import { Divider, Link, makeStyles, Theme } from '@material-ui/core';
import gfm from 'remark-gfm';
import ReactMarkdown, { Components } from 'react-markdown';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { ReactMarkdownOptions } from 'react-markdown/lib/react-markdown';

type BackstageTypography = Theme['typography'] & {
  htmlFontSize?: number;
};

const useStyles = makeStyles(theme => {
  const htmlFontSize =
    (theme.typography as BackstageTypography).htmlFontSize ?? 16;
  const calculateHeadingStyle = (styles: CSSProperties) => {
    const { lineHeight, fontFamily, fontWeight, fontSize } = styles;
    let factor: number | string = 1;
    if (typeof fontSize === 'number') {
      // convert px to rem
      // 60% of the size defined because it is too big
      factor = (fontSize / htmlFontSize) * 0.6;
    }
    if (typeof fontSize === 'string') {
      factor = fontSize.replace('rem', '');
    }
    return {
      lineHeight,
      fontFamily,
      fontWeight,
      fontSize: `calc(${factor} * 1rem)`,
    };
  };
  return {
    markdown: {
      '& h1': calculateHeadingStyle(theme.typography.h1),
      '& h2': calculateHeadingStyle(theme.typography.h2),
      '& h3': calculateHeadingStyle(theme.typography.h3),
      '& h4': calculateHeadingStyle(theme.typography.h4),
      '& h5': calculateHeadingStyle(theme.typography.h5),
      '& h6': calculateHeadingStyle(theme.typography.h6),
    },
  };
});

/**
 * Adapted from https://github.com/remarkjs/react-markdown/issues/404#issuecomment-604019030
 */
const flatten = (text: string, child: any): string => {
  if (!child) return text;
  return typeof child === 'string'
    ? text + child
    : Children.toArray(child.props.children).reduce(flatten, text);
};

const Anchor: FC<{ href?: string }> = props => (
  <Link {...props} target="_blank" />
);

type MarkdownContentProps = ReactMarkdownOptions & {
  headingOffset?: number;
  components?: Components;
};

export const MarkdownContent = (props: MarkdownContentProps) => {
  const classes = useStyles();
  const { children, headingOffset, components, ...rest } = props;

  /**
   * Adapted from https://github.com/remarkjs/react-markdown/issues/404#issuecomment-604019030
   * @note HeadingComponent is not compatible with children?: React.ReactNode in props signature
   */
  // @ts-expect-error
  const Heading: FC<{ level: number }> = ({ level, children }) => {
    if (headingOffset) {
      level = Math.max(1, Math.min(6, level + headingOffset));
    }
    const childrenArray = Children.toArray(children);
    const text = childrenArray.reduce(flatten, '');
    const slug = text.toLocaleLowerCase('en-US').replace(/\W/g, '-');
    return createElement(`h${level}`, { id: slug }, children);
  };
  return (
    <ReactMarkdown
      linkTarget="_blank"
      remarkPlugins={[gfm]}
      components={{
        h1: Heading,
        h2: Heading,
        h3: Heading,
        h4: Heading,
        h5: Heading,
        h6: Heading,
        hr: Divider,
        a: Anchor,
        ...(components ? components : {}),
      }}
      className={classes.markdown}
      {...rest}
    >
      {children}
    </ReactMarkdown>
  );
};
